import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Minus, Trash, House, User } from "@phosphor-icons/react";

const blankPropietario = () => ({
  nombre: "", sexo: "M", nacionalidad: "dominicano", estado_civil: "soltero",
  cedula: "", domicilio: "",
});
const blankTestigo = () => ({
  nombre: "", sexo: "M", nacionalidad: "dominicano", estado_civil: "soltero",
  cedula: "", domicilio: "",
});
const blankMejora = () => ({
  cantidad: 1, tipo: "", paredes: "BLOCKS", techo: "ALUZINC", piso: "CERAMICA", area_m2: "",
});

export default function PosesionTab({ exp, setExp }) {
  const pos = exp.posesion || {};
  const upd = (key, value) => setExp({ ...exp, posesion: { ...pos, [key]: value } });
  const updCol = (key, value) =>
    setExp({ ...exp, posesion: { ...pos, colindancias: { ...(pos.colindancias || {}), [key]: value } } });

  const propietarios = pos.propietarios || [];
  const testigos = pos.testigos || [];
  const mejoras = pos.mejoras || [];
  const col = pos.colindancias || {};

  return (
    <div className="space-y-6" data-testid="posesion-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
        <div className="coord-label text-emerald-800">// TRÁMITE</div>
        <h3 className="text-lg font-bold tracking-tight text-emerald-900">
          DECLARACIÓN JURADA DE POSESIÓN Y MEJORA
        </h3>
        <p className="text-sm text-emerald-800 mt-1">
          Captura los <b>propietarios/poseedores</b>, los <b>7 testigos</b> y las{" "}
          <b>mejoras</b> sobre el inmueble. El notario se edita en el tab "Datos".
          Los testigos por defecto se configuran en tu Perfil.
        </p>
      </div>

      {/* Datos del acto */}
      <div className="bg-white border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-3">Datos del Acto</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="coord-label">Acto No.</Label>
            <Input
              data-testid="pos-acto-numero"
              value={pos.acto_numero || ""}
              onChange={(e) => upd("acto_numero", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="01, 02, ..."
            />
          </div>
          <div>
            <Label className="coord-label">Fecha del Acto (DD/MM/AAAA)</Label>
            <Input
              data-testid="pos-fecha-acto"
              value={pos.fecha_acto || ""}
              onChange={(e) => upd("fecha_acto", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="14/01/2025"
            />
          </div>
          <div>
            <Label className="coord-label">Años de Posesión</Label>
            <Input
              data-testid="pos-anios"
              value={pos.anios_posesion || ""}
              onChange={(e) => upd("anios_posesion", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="15"
            />
          </div>
        </div>
      </div>

      {/* Propietarios */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-bold text-slate-900">Propietarios / Poseedores</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Agrega 1 o más (ej: cónyuges). Son los sujetos de la declaración.
            </p>
          </div>
          <Button
            data-testid="pos-add-propietario"
            size="sm" variant="outline"
            onClick={() => upd("propietarios", [...propietarios, blankPropietario()])}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Agregar propietario
          </Button>
        </div>
        {propietarios.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            Sin propietarios. Haz clic en "Agregar propietario" para iniciar.
          </div>
        ) : (
          <div className="space-y-3">
            {propietarios.map((p, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`pos-propietario-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500 flex items-center gap-1">
                    <User size={12} /> PROPIETARIO #{i + 1}
                  </span>
                  <Button
                    data-testid={`pos-propietario-del-${i}`}
                    size="sm" variant="ghost"
                    onClick={() => upd("propietarios", propietarios.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  ><Minus size={14} /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`pos-prop-nombre-${i}`}
                    value={p.nombre || ""}
                    onChange={(e) => {
                      const next = [...propietarios]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("propietarios", next);
                    }}
                    placeholder="Nombre completo"
                    className="rounded-sm md:col-span-2"
                  />
                  <Select
                    value={p.sexo || "M"}
                    onValueChange={(v) => {
                      const next = [...propietarios];
                      next[i] = {
                        ...next[i],
                        sexo: v,
                        // Si está en defaults, auto-cambiar concordancia
                        nacionalidad: v === "F"
                          ? (next[i].nacionalidad === "dominicano" ? "dominicana" : next[i].nacionalidad)
                          : (next[i].nacionalidad === "dominicana" ? "dominicano" : next[i].nacionalidad),
                        estado_civil: v === "F"
                          ? (next[i].estado_civil === "soltero" ? "soltera" : next[i].estado_civil === "casado" ? "casada" : next[i].estado_civil)
                          : (next[i].estado_civil === "soltera" ? "soltero" : next[i].estado_civil === "casada" ? "casado" : next[i].estado_civil),
                      };
                      upd("propietarios", next);
                    }}
                  >
                    <SelectTrigger data-testid={`pos-prop-sexo-${i}`} className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid={`pos-prop-cedula-${i}`}
                    value={p.cedula || ""}
                    onChange={(e) => {
                      const next = [...propietarios]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("propietarios", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    data-testid={`pos-prop-estado-${i}`}
                    value={p.estado_civil || ""}
                    onChange={(e) => {
                      const next = [...propietarios]; next[i] = { ...next[i], estado_civil: e.target.value };
                      upd("propietarios", next);
                    }}
                    placeholder={p.sexo === "F" ? "soltera/casada/viuda..." : "soltero/casado/viudo..."}
                    className="rounded-sm"
                  />
                  <Input
                    data-testid={`pos-prop-domicilio-${i}`}
                    value={p.domicilio || ""}
                    onChange={(e) => {
                      const next = [...propietarios]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("propietarios", next);
                    }}
                    placeholder="Domicilio completo"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mejoras */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-bold text-slate-900">Mejoras / Edificaciones</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Casas, gazebos, construcciones. Se listarán en el acto.
            </p>
          </div>
          <Button
            data-testid="pos-add-mejora"
            size="sm" variant="outline"
            onClick={() => upd("mejoras", [...mejoras, blankMejora()])}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Agregar mejora
          </Button>
        </div>
        {mejoras.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            Sin mejoras.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-2 py-2 text-left w-12">Cant.</th>
                  <th className="px-2 py-2 text-left">Tipo</th>
                  <th className="px-2 py-2 text-left">Paredes</th>
                  <th className="px-2 py-2 text-left">Techo</th>
                  <th className="px-2 py-2 text-left">Piso</th>
                  <th className="px-2 py-2 text-left w-24">Área m²</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {mejoras.map((m, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`pos-mejora-${i}`}>
                    <td className="px-2 py-1">
                      <Input
                        data-testid={`pos-mejora-cant-${i}`}
                        value={m.cantidad ?? 1}
                        onChange={(e) => {
                          const next = [...mejoras]; next[i] = { ...next[i], cantidad: e.target.value };
                          upd("mejoras", next);
                        }}
                        type="number" min="1"
                        className="rounded-sm h-8 font-mono"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        data-testid={`pos-mejora-tipo-${i}`}
                        value={m.tipo || ""}
                        onChange={(e) => {
                          const next = [...mejoras]; next[i] = { ...next[i], tipo: e.target.value };
                          upd("mejoras", next);
                        }}
                        placeholder="CASA DE UN NIVEL"
                        className="rounded-sm h-8"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        value={m.paredes || ""}
                        onChange={(e) => {
                          const next = [...mejoras]; next[i] = { ...next[i], paredes: e.target.value };
                          upd("mejoras", next);
                        }}
                        placeholder="BLOCKS"
                        className="rounded-sm h-8"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        value={m.techo || ""}
                        onChange={(e) => {
                          const next = [...mejoras]; next[i] = { ...next[i], techo: e.target.value };
                          upd("mejoras", next);
                        }}
                        placeholder="ALUZINC"
                        className="rounded-sm h-8"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        value={m.piso || ""}
                        onChange={(e) => {
                          const next = [...mejoras]; next[i] = { ...next[i], piso: e.target.value };
                          upd("mejoras", next);
                        }}
                        placeholder="CERAMICA"
                        className="rounded-sm h-8"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        data-testid={`pos-mejora-area-${i}`}
                        value={m.area_m2 || ""}
                        onChange={(e) => {
                          const next = [...mejoras]; next[i] = { ...next[i], area_m2: e.target.value };
                          upd("mejoras", next);
                        }}
                        placeholder="230.57"
                        className="rounded-sm h-8 font-mono"
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <Button
                        data-testid={`pos-mejora-del-${i}`}
                        size="sm" variant="ghost"
                        onClick={() => upd("mejoras", mejoras.filter((_, idx) => idx !== i))}
                        className="rounded-sm text-red-500 h-7 px-2"
                      ><Trash size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ubicación y ámbito de parcela */}
      <div className="bg-white border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
          <House size={16} /> Ubicación del Inmueble
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="coord-label">Dirección / Ubicación</Label>
            <Input
              data-testid="pos-ubicacion"
              value={pos.ubicacion_inmueble || ""}
              onChange={(e) => upd("ubicacion_inmueble", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="Camino Baiguate No. 70, Paso Bajito, Jarabacoa"
            />
          </div>
          <div>
            <Label className="coord-label">Parcela No. (ámbito)</Label>
            <Input
              data-testid="pos-parcela"
              value={pos.parcela_ambito || ""}
              onChange={(e) => upd("parcela_ambito", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="42"
            />
          </div>
          <div>
            <Label className="coord-label">D.C.</Label>
            <Input
              data-testid="pos-dc"
              value={pos.dc_ambito || ""}
              onChange={(e) => upd("dc_ambito", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="04"
            />
          </div>
          <div>
            <Label className="coord-label">Municipio</Label>
            <Input
              data-testid="pos-municipio"
              value={pos.municipio_ambito || ""}
              onChange={(e) => upd("municipio_ambito", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="Jarabacoa"
            />
          </div>
          <div>
            <Label className="coord-label">Provincia</Label>
            <Input
              data-testid="pos-provincia"
              value={pos.provincia || ""}
              onChange={(e) => upd("provincia", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="La Vega"
            />
          </div>
          <div>
            <Label className="coord-label">Área Total del Terreno (m²)</Label>
            <Input
              data-testid="pos-area-total"
              value={pos.area_total_terreno || ""}
              onChange={(e) => upd("area_total_terreno", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="19,668.10"
            />
          </div>
          <div>
            <Label className="coord-label">Valor Aproximado (RD$)</Label>
            <Input
              data-testid="pos-valor"
              value={pos.valor_aproximado || ""}
              onChange={(e) => upd("valor_aproximado", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="500,000.00"
            />
          </div>
          <div>
            <Label className="coord-label">Destino / Uso</Label>
            <Input
              data-testid="pos-destino"
              value={pos.destino_uso || ""}
              onChange={(e) => upd("destino_uso", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="vivienda familiar"
            />
          </div>
          <div>
            <Label className="coord-label">Titular del Terreno</Label>
            <Input
              data-testid="pos-titular"
              value={pos.titular_terreno || ""}
              onChange={(e) => upd("titular_terreno", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="ESTADO DOMINICANO"
            />
          </div>
        </div>

        {/* Colindancias */}
        <div className="mt-4">
          <Label className="coord-label">Colindancias</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
            <Input
              data-testid="pos-col-norte"
              value={col.norte || ""}
              onChange={(e) => updCol("norte", e.target.value)}
              className="rounded-sm"
              placeholder="AL NORTE: Resto de la misma parcela"
            />
            <Input
              data-testid="pos-col-este"
              value={col.este || ""}
              onChange={(e) => updCol("este", e.target.value)}
              className="rounded-sm"
              placeholder="AL ESTE: Resto de la misma parcela"
            />
            <Input
              data-testid="pos-col-sur"
              value={col.sur || ""}
              onChange={(e) => updCol("sur", e.target.value)}
              className="rounded-sm"
              placeholder="AL SUR: Camino Baiguate"
            />
            <Input
              data-testid="pos-col-oeste"
              value={col.oeste || ""}
              onChange={(e) => updCol("oeste", e.target.value)}
              className="rounded-sm"
              placeholder="AL OESTE: P. No. 6"
            />
          </div>
        </div>
      </div>

      {/* Testigos */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-bold text-slate-900">Testigos del Acto (típicamente 7)</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Si dejas vacío, se usan los testigos por defecto del Perfil (sección "Posesión / Mejora").
            </p>
          </div>
          <Button
            data-testid="pos-add-testigo"
            size="sm" variant="outline"
            onClick={() => upd("testigos", [...testigos, blankTestigo()])}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Sobrescribir testigo
          </Button>
        </div>
        {testigos.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            Usando los testigos del Perfil.
          </div>
        ) : (
          <div className="space-y-3">
            {testigos.map((t, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`pos-testigo-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">TESTIGO #{i + 1}</span>
                  <Button
                    data-testid={`pos-testigo-del-${i}`}
                    size="sm" variant="ghost"
                    onClick={() => upd("testigos", testigos.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  ><Minus size={14} /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`pos-testigo-nombre-${i}`}
                    value={t.nombre || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Nombre completo"
                    className="rounded-sm"
                  />
                  <Select
                    value={t.sexo || "M"}
                    onValueChange={(v) => {
                      const next = [...testigos];
                      next[i] = {
                        ...next[i],
                        sexo: v,
                        nacionalidad: v === "F"
                          ? (next[i].nacionalidad === "dominicano" ? "dominicana" : next[i].nacionalidad)
                          : (next[i].nacionalidad === "dominicana" ? "dominicano" : next[i].nacionalidad),
                        estado_civil: v === "F"
                          ? (next[i].estado_civil === "soltero" ? "soltera" : next[i].estado_civil === "casado" ? "casada" : next[i].estado_civil)
                          : (next[i].estado_civil === "soltera" ? "soltero" : next[i].estado_civil === "casada" ? "casado" : next[i].estado_civil),
                      };
                      upd("testigos", next);
                    }}
                  >
                    <SelectTrigger data-testid={`pos-testigo-sexo-${i}`} className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid={`pos-testigo-cedula-${i}`}
                    value={t.cedula || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    value={t.estado_civil || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], estado_civil: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder={t.sexo === "F" ? "soltera/casada/viuda..." : "soltero/casado/viudo..."}
                    className="rounded-sm"
                  />
                  <Input
                    value={t.domicilio || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Domicilio"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compulsa Registro (opcional) */}
      <div className="bg-white border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-1">Registro de Hipotecas (opcional, para la Compulsa)</h4>
        <p className="text-xs text-slate-500 mb-3">
          Datos del sello del Conservador de Hipotecas (se imprimen al final de la Compulsa).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="coord-label">No. de Registro</Label>
            <Input
              data-testid="pos-compulsa-num"
              value={pos.compulsa_registro_numero || ""}
              onChange={(e) => upd("compulsa_registro_numero", e.target.value)}
              className="mt-1 rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="coord-label">Libro Letra</Label>
            <Input
              value={pos.compulsa_registro_libro || ""}
              onChange={(e) => upd("compulsa_registro_libro", e.target.value)}
              className="mt-1 rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="coord-label">Folio</Label>
            <Input
              value={pos.compulsa_registro_folio || ""}
              onChange={(e) => upd("compulsa_registro_folio", e.target.value)}
              className="mt-1 rounded-sm font-mono"
            />
          </div>
          <div>
            <Label className="coord-label">Fecha Registro (DD/MM/AAAA)</Label>
            <Input
              value={pos.compulsa_fecha_registro || ""}
              onChange={(e) => upd("compulsa_fecha_registro", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="14/01/2025"
            />
          </div>
          <div>
            <Label className="coord-label">Fecha de Expedición (DD/MM/AAAA)</Label>
            <Input
              value={pos.compulsa_fecha_expedicion || ""}
              onChange={(e) => upd("compulsa_fecha_expedicion", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="14/01/2025"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
